import { createSlice } from '@reduxjs/toolkit';
import client from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { RootState } from 'redux/store';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { fetchStaffList } from 'services/fetchStaffList';
import { ID } from 'types/common';
import { CreateStaff } from 'types/staff';
import { getDefaultCompanyId } from 'utils/helpers';

interface Staff {
    username: string;
    firstName: string;
    lastName: string;
    name: string;
    uuid: string;
    status: string;
    createdDate: string;
    updatedDate: string;
}
interface StaffList {
    activeRecords: Staff[];
    inactiveRecords: Staff[];
    pendingRecords: Staff[];
    deletedRecords: Staff[];
    loading: boolean;
    error: string | null | boolean;
}

export type SetStatus = 'ACTIVE' | 'DELETED' | 'INACTIVE' | 'ALL' | 'PENDING';

const initialState: StaffList = {
    activeRecords: [],
    inactiveRecords: [],
    pendingRecords: [],
    deletedRecords: [],
    loading: false,
    error: null,
};

export interface GetStaffListPayload {
    setStatus: SetStatus;
}
export interface CreateStaffPayload {
    formValues: CreateStaff;
}
export interface FetchUSerDetailsPayload {
    username: string;
}
export const getStaffList = createAsyncThunkWrapper<any, GetStaffListPayload>(
    'active/getStaffList',
    async ({ setStatus }: GetStaffListPayload, { getState }) => {
        const state: any = await getState();
        const defaultCompanyId = getDefaultCompanyId(state);
        if (!defaultCompanyId) {
            return;
        }
        const sortedEmployees = await fetchStaffList(defaultCompanyId, setStatus);
        return { type: setStatus, list: sortedEmployees || [] };
    }
);

export const createStaff = createAsyncThunkWrapper<any, CreateStaffPayload>(
    'staff/createStaff',
    async ({ formValues }: CreateStaffPayload, { getState }) => {
        const state: any = await getState();
        const defaultCompanyId = getDefaultCompanyId(state);
        if (!defaultCompanyId) {
            return;
        }
        // adding new key as username because back end require the username but not in figma
        const _formValues = { ...formValues, username: formValues?.email };
        const response: any = await client.post('/merchant/createEmployee', _formValues, {
            companyId: defaultCompanyId,
            timezone: 'America/Chicago',
        });
        return response;
    }
);

export const removeStaff = createAsyncThunkWrapper<any, ID>('staff/removeStaff', async (uuid: ID, { getState }) => {
    const state: any = await getState();
    const companyId = getDefaultCompanyId(state);
    if (!companyId) {
        return;
    }
    const response: any = await client.delete(`/merchant/removeEmployee/${uuid}`, {
        headers: {
            companyId,
        },
    });
    if (response.code === 200) {
        notify('User Removed successfully', 'success');
    }
    return response;
});

export const fetchUserDetails = createAsyncThunkWrapper<any, FetchUSerDetailsPayload>(
    'staffDetail/fetchUserDetails',
    async ({ username }: FetchUSerDetailsPayload, { getState }) => {
        const state = getState() as RootState;
        const companyId = getDefaultCompanyId(state);
        const response: any = await client.get('/user/getUser', {
            headers: {
                username,
                companyId,
            },
        });
        const { msg, statusCode, code, ...staffDetails } = response;
        return staffDetails;
    }
);

const staffSlice = createSlice({
    name: 'activeStaff',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getStaffList.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getStaffList.fulfilled, (state, action) => {
            const { payload } = action;
            state.loading = false;
            if (!payload) return;
            const { list, type } = payload;
            switch (type) {
                case 'ACTIVE':
                    state.activeRecords = list;
                    break;
                case 'INACTIVE':
                    state.inactiveRecords = list;
                    break;
                case 'PENDING':
                    state.pendingRecords = list;
                    break;
                case 'DELETED':
                    state.deletedRecords = list;
                    break;
                default:
                    state.activeRecords = list;
            }
        });
        builder.addCase(getStaffList.rejected, (state) => {
            state.loading = false;
            state.error = true;
        });
    },
});

export default staffSlice.reducer;
